import type { UserRole } from '@kfolks/types'
import { isAdmin, roleAtLeast } from '@/auth/role-order'
import type { NavigationGroup, NavigationItem } from './config.js'

export interface NavigationViewer {
	role: UserRole
	hadEverPendingApproval: boolean
}

/**
 * Closed-set predicate evaluators for `visibleIf` strings.
 * Extend this map when adding a new predicate — do not accept arbitrary functions.
 */
const PREDICATES: Record<string, (viewer: NavigationViewer) => boolean> = {
	'has-ever-had-pending-approval-or-admin': (viewer) => viewer.hadEverPendingApproval || isAdmin(viewer.role),
}

class UnknownNavigationPredicateError extends Error {
	constructor(predicate: string) {
		super(`Unknown navigation predicate: "${predicate}". Add it to PREDICATES in filterNav.ts.`)
		this.name = 'UnknownNavigationPredicateError'
	}
}

function isItemVisible(item: NavigationItem, viewer: NavigationViewer): boolean {
	if (item.visibleFor && !item.visibleFor.includes(viewer.role)) return false
	if (item.minRole && !roleAtLeast(viewer.role, item.minRole)) return false

	if (item.visibleIf) {
		const evaluator = PREDICATES[item.visibleIf]
		if (!evaluator) throw new UnknownNavigationPredicateError(item.visibleIf)
		if (!evaluator(viewer)) return false
	}

	return true
}

function isGroupVisible(group: NavigationGroup, viewer: NavigationViewer): boolean {
	if (group.visibleFor && !group.visibleFor.includes(viewer.role)) return false
	return true
}

export function filterNav(config: NavigationGroup[], viewer: NavigationViewer): NavigationGroup[] {
	return config
		.filter((group) => isGroupVisible(group, viewer))
		.map((group) => ({
			...group,
			items: group.items.filter((item) => isItemVisible(item, viewer)),
		}))
}
